import { BadRequestException, Injectable } from '@nestjs/common';
import { resolveFrontendOrigin } from './frontend-origin.util';

export interface GoogleProfile {
  googleId: string;
  email: string;
  name: string;
}

// 'merchant' intent routes the callback through loginWithGoogleMerchant()
// (never auto-creates, requires an already-verified merchant account);
// omitted/'customer' keeps today's auto-create behavior.
export type GoogleOAuthIntent = 'customer' | 'merchant';

interface DecodedGoogleState {
  origin: string;
  intent: GoogleOAuthIntent;
}

// Same env-var-with-fallback pattern as the rest of this codebase's optional
// integrations (e.g. Razorpay keys in .env.example) — boot never fails just
// because Google OAuth isn't configured yet; hitting /auth/google simply
// redirects to a Google URL that will reject the unconfigured client id.
@Injectable()
export class GoogleOAuthService {
  private readonly clientId = process.env.GOOGLE_CLIENT_ID ?? 'not-configured';
  private readonly clientSecret = process.env.GOOGLE_CLIENT_SECRET ?? 'not-configured';
  private readonly callbackUrl =
    process.env.GOOGLE_CALLBACK_URL ?? 'http://localhost:5103/api/auth/google/callback';
  private readonly defaultFrontendOrigin =
    process.env.DEFAULT_FRONTEND_ORIGIN ?? 'http://localhost:4200';

  /** Full Google consent-screen URL. `returnOrigin` + `intent` are validated + carried through as `state`. */
  buildAuthorizeUrl(returnOrigin: string | undefined, intent: GoogleOAuthIntent = 'customer'): string {
    const raw = `${this.resolveOrigin(returnOrigin)}::${intent}`;
    const state = Buffer.from(raw, 'utf8').toString('base64url');
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.callbackUrl,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      prompt: 'select_account',
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  /** Decodes the `state` round-tripped by Google back into a safe frontend origin + the original intent. */
  resolveState(state: string | undefined): DecodedGoogleState {
    try {
      const decoded = Buffer.from(state ?? '', 'base64url').toString('utf8');
      const [origin, intent] = decoded.split('::');
      return {
        origin: this.resolveOrigin(origin),
        intent: intent === 'merchant' ? 'merchant' : 'customer',
      };
    } catch {
      return { origin: this.defaultFrontendOrigin, intent: 'customer' };
    }
  }

  /** Decodes just the origin — kept for callers that redirect on failure before intent matters. */
  resolveRedirectOrigin(state: string | undefined): string {
    return this.resolveState(state).origin;
  }

  async exchangeCode(code: string): Promise<GoogleProfile> {
    const tokenRes = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        code,
        client_id: this.clientId,
        client_secret: this.clientSecret,
        redirect_uri: this.callbackUrl,
        grant_type: 'authorization_code',
      }),
    });

    if (!tokenRes.ok) {
      throw new BadRequestException('Google token exchange failed');
    }

    const { access_token: accessToken } = (await tokenRes.json()) as { access_token: string };

    const profileRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!profileRes.ok) {
      throw new BadRequestException('Failed to fetch Google profile');
    }

    const profile = (await profileRes.json()) as { sub: string; email?: string; name?: string };

    if (!profile.email) {
      throw new BadRequestException('Google account has no public email');
    }

    return {
      googleId: profile.sub,
      email: profile.email.toLowerCase(),
      name: profile.name?.trim() || profile.email,
    };
  }

  private resolveOrigin(candidate: string | undefined): string {
    return resolveFrontendOrigin(candidate);
  }
}
